import React from 'react';
import { Tag } from '@decisiv/ui-components';

// Renders a usage badge from oauth_applications.last_used_at, which is stamped
// (at most once/day) whenever the app mints or refreshes a token at
// /oauth/token. Thresholds are provisional display heuristics — the eventual
// dormant-app expiry policy will define the authoritative cutoff.
const usageStatusTag = (record) => {
  const lastUsedAt = record.last_used_at;
  if (!lastUsedAt) return <Tag intent="neutral" text="Never used" />;

  // last_used_at is a DATE (no time). Diff calendar days from the date parts in
  // local time — parsing the string as a timestamp would treat it as UTC
  // midnight and, against a local Date.now(), drift by a day around time-zone /
  // DST boundaries, misclassifying apps right at the 30/90-day cutoffs.
  const [year, month, day] = lastUsedAt.split('-').map(Number);
  const usedMidnight = new Date(year, month - 1, day).getTime();
  const todayMidnight = new Date().setHours(0, 0, 0, 0);
  const daysAgo = Math.round((todayMidnight - usedMidnight) / 86400000);

  if (daysAgo <= 30) return <Tag intent="success" text="Active" />;
  if (daysAgo <= 90) return <Tag intent="warning" text="Idle" />;
  return <Tag intent="danger" text="Dormant" />;
};

const oauthApplicationConfig = {
  name: 'OAuth Application',
  endpoint: '/internal_admin/admin_kit/oauth_applications',
  modelKey: 'doorkeeper_application',

  columns: [
    { name: 'name', title: 'Name', type: 'link' },
    { name: 'uid', title: 'UID' },
    { name: 'scopes', title: 'Scopes' },
    { name: 'confidential', title: 'Confidential', type: 'boolean' },
    { name: 'decisiv_trusted', title: 'Decisiv Trusted', type: 'boolean' },
    {
      name: 'password_grant_allowed',
      title: 'Password Grant',
      type: 'boolean',
    },
    { name: 'last_used_at', title: 'Last Used', type: 'date' },
    { name: 'usage_status', title: 'Usage', render: usageStatusTag },
    { name: 'created_at', title: 'Created' },
  ],

  filters: [
    { name: 'q[name_cont]', label: 'Name' },
    { name: 'q[uid_cont]', label: 'UID' },
    {
      // Backed by the Doorkeeper::Application.by_usage_status ransackable
      // scope; options come from the controller's index_component_props.
      name: 'q[by_usage_status]',
      label: 'Usage',
      options: (props) => props.usageStatusOptions ?? [],
    },
  ],

  attributes: [
    { name: 'id', label: 'ID' },
    { name: 'name', label: 'Name' },
    { name: 'uid', label: 'UID' },
    { name: 'secret', label: 'Secret' },
    { name: 'redirect_uri', label: 'Redirect URI' },
    { name: 'scopes', label: 'Scopes' },
    { name: 'modules', label: 'Modules' },
    { name: 'confidential', label: 'Confidential', type: 'boolean' },
    { name: 'decisiv_trusted', label: 'Decisiv Trusted', type: 'boolean' },
    {
      name: 'password_grant_allowed',
      label: 'Password Grant Allowed',
      type: 'boolean',
    },
    { name: 'last_used_at', label: 'Last Used At', type: 'date' },
    { name: 'usage_status', label: 'Usage', render: usageStatusTag },
    { name: 'created_at', label: 'Created At' },
    { name: 'updated_at', label: 'Updated At' },
  ],

  panels: [
    {
      title: 'API Role Assignments',
      endpoint: (record) =>
        `/internal_admin/admin_kit/oauth_applications/${record.id}/api_role_assignments`,
      columns: [
        { name: 'api_role', title: 'API Role' },
        { name: 'created_by', title: 'Created By' },
        { name: 'created_at', title: 'Created At' },
      ],
    },
  ],

  form: {
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      {
        name: 'scopes',
        label: 'Scopes',
        type: 'text',
        defaultValue: 'public',
        helpMessage: 'Space-separated list (default: public)',
      },
      {
        name: 'redirect_uri',
        label: 'Redirect URI',
        type: 'textarea',
        required: true,
        rows: 3,
        span: 2,
        helpMessage:
          'Use urn:ietf:wg:oauth:2.0:oob for localhost / native-app development',
      },
      {
        name: 'modules',
        label: 'Modules',
        type: 'text',
        helpMessage: 'Comma-separated API modules this application may access',
      },
      {
        name: 'confidential',
        label: 'Confidential',
        type: 'checkbox',
        defaultValue: true,
      },
      {
        name: 'decisiv_trusted',
        label: 'Decisiv Trusted',
        type: 'checkbox',
        defaultValue: false,
      },
      {
        name: 'password_grant_allowed',
        label: 'Password Grant Allowed',
        type: 'checkbox',
        defaultValue: false,
        // One-way ratchet: never shown on the New form (new apps are always
        // deny-by-default), and only shown on Edit when the grant is currently
        // enabled — so it can be turned OFF here but never (re-)enabled.
        editOnly: true,
        showOnEdit: (record) => record.password_grant_allowed === true,
        helpMessage:
          'Uncheck to permanently disable the OAuth password grant for this application. ' +
          'It cannot be re-enabled here — applications should use the authorization_code flow.',
      },
    ],
  },
};

export default oauthApplicationConfig;
